import {AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, viewChild} from '@angular/core';
import {BaseTaskComponent} from '../base-task-component';
import {AdaptiveCardTaskService} from './services/adaptive-card-task.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TaskStepAdaptiveCardViewModel} from '../../view-models/task-step-adaptive-card.view-model';
import {StepHeaderComponent} from '@shared/components/step-header/step-header.component';
import {prepareAdaptiveCardDataForTransport, renderAdaptiveCard} from '@shared/utilities/adaptive-card-renderer';
import {ModalService} from '@shared/services/modal.service';
import {ErrorModalComponent} from '@shared/components/error-modal/error-modal.component';
import {STEP_SUBMIT_GENERIC_ERROR} from '@shared/utilities/step-submit-error';

@Component({
    selector: 'app-adaptive-card-task',
    templateUrl: './adaptive-card-task.component.html',
    styleUrls: ['./adaptive-card-task.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [StepHeaderComponent]
})
export class AdaptiveCardTaskComponent extends BaseTaskComponent implements AfterViewInit {

  private readonly destroyRef = inject(DestroyRef);
  private readonly adaptiveCardTaskService = inject(AdaptiveCardTaskService);
  private readonly modalService = inject(ModalService);

  readonly container = viewChild.required<ElementRef>('container');

  ngAfterViewInit(): void {
    const stepDetail = this.step() as TaskStepAdaptiveCardViewModel;

    renderAdaptiveCard(stepDetail, this.container(), this.task().readonly, {
      onBack: () => this.previous(),
      onNextOrFinish: (data) => this.task().readonly ? this.next() : this.save(data),
      onClose: () => this.close()
    });
  }

  private save(adaptiveCardData: Record<string, string>) {
    if (this.processing) {
      return;
    }

    this.processing = true;

    const sanitizedData = this.sanitizeActionData(adaptiveCardData);

    this.adaptiveCardTaskService.save(this.task().id, this.step().id, sanitizedData).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: updatedTask => {
        this.updateTask(updatedTask);
        this.processing = false;
        this.next();
      },
      error: () => {
        this.processing = false;
        this.modalService.openModal(ErrorModalComponent, STEP_SUBMIT_GENERIC_ERROR);
      }
    });
  }

  /** Delegates to shared utility. Kept as instance method for existing test compatibility. */
  private sanitizeActionData(data: Record<string, string>): Record<string, string> {
    return prepareAdaptiveCardDataForTransport(data);
  }
}
